import React, {useEffect, useRef, useState} from 'react';
import PropTypes from "prop-types";
import {useNavigate} from "react-router-dom";

import {FormProgressContext} from "@/contexts/FormProgressContext.jsx";
import {useLocalStorage} from "@/hooks/useLocalStorage.jsx";
import {styled} from "@mui/material/styles";
import {COLOR_BLUE} from "@/themes/colors.jsx";

export const FormProgressProvider = ({
                                         initialStepsCount,
                                         flows = [],
                                         openedRoutes = [],
                                         allowedSaveToStackRoutes = [],
                                         progressLine = false,
                                         progressLineStartStep = 1,
                                         children
                                     }) => {
    const navigate = useNavigate();
    const {storeData, getStoredData} = useLocalStorage() || {
        storeData: () => {
        },
        getStoredData: () => {
        },
    };

    const [progressDisplay, setProgressDisplay] = useState(false);
    const [stepsCount, setStepsCount] = useState(initialStepsCount ?? 1);
    const [distance, setDistance] = useState(100 / stepsCount);
    const [currentProgress, setCurrentProgress] = useState(0);

    const stackRef = useRef([...openedRoutes]);
    const pathRef = useRef(null);

    useEffect(() => {
        const storedData = getStoredData();

        if (storedData?.progress_stack?.length) {
            if (JSON.stringify(storedData?.progress_stack) !== JSON.stringify(stackRef.current)) {
                storedData?.progress_stack.forEach(route => stackRef.current.push(route));
            }
        }

        if (storedData?.progress_path?.length && storedData.progress_path != pathRef.current) {
            if (allowedSaveToStackRoutes.includes(storedData.progress_path)) {
                if (flows?.length > 0) {
                    const flow = findFlow(storedData.progress_path);
                    if (flow?.length > 0) {
                        for (const progress of flow) {
                            if (progress === storedData.progress_path) break;
                            progressNavigate(progress)
                        }
                    }
                }
                progressNavigate(storedData.progress_path)
            } else {
                progressNavigate(storedData?.progress_stack?.pop())
            }
        }
    }, [getStoredData]);

    useEffect(() => {
        if(currentProgress > 0) {
            window.scrollTo(0, 0);
        }
    }, [currentProgress])

    useEffect(() => {
        setDistance(100 / stepsCount)
    }, [stepsCount])

    const isOpenedStep = (path) => {
        return isInStack(path);
    }

    const getOpenedPath = () => {
        return openedRoutes[0];
    }

    const getCurrentRoute = () => {
        return pathRef.current;
    }

    const setProgress = (progress) => {
        if (!progressDisplay && progress > 0) {
            setProgressDisplay(true);
        } else if (progressDisplay && progress == 0) {
            setProgressDisplay(false);
        }

        setCurrentProgress(progress);
    }

    const addRouteToStack = (route) => {
        if (!isInStack(route)) {
            if (allowedSaveToStackRoutes.includes(route)) {
                const storageStack = getStoredData()?.progress_stack ?? [];
                storageStack.push(route);
                storeData('progress_stack', storageStack);
            }

            stackRef.current.push(route);
        }
    }

    const isInStack = (route) => {
        return stackRef.current.includes(route);
    }

    const progressNavigate = (route, options = {}) => {
        pathRef.current = route;

        addRouteToStack(route);
        storeData('progress_path', route);

        navigate(route, options);
    }

    const findFlow = (searchItem) => {
        for (const flow of flows) {
            if (flow.includes(searchItem)) {
                return flow;
            }
        }
        return null;
    };

    return (
        <FormProgressContext.Provider
            value={{
                progressDisplay,
                stepsCount,
                currentProgress,
                setStepsCount,
                setProgressDisplay,
                setCurrentProgress: setProgress,
                navigate: progressNavigate,
                isOpenedStep,
                getOpenedPath,
                getCurrentRoute,
            }}>
            {progressLine && currentProgress >= progressLineStartStep && (
                <ProgressLine>
                    <Line width={(currentProgress - 1) * distance}/>
                </ProgressLine>
            )}
            {children}
        </FormProgressContext.Provider>
    );
};

FormProgressContext.propTypes = {
    children: PropTypes.any,
    openedRoutes: PropTypes.array,
    allowedSaveToStackRoutes: PropTypes.array,
};

const ProgressLine = styled('div')({
    position: 'absolute',
    height: '4px',
    backgroundColor: '#00000005',
    width: '100%',
    top: '0',
    left: '0',
});

const Line = styled('div')((props) => ({
    height: '4px',
    backgroundColor: COLOR_BLUE,
    width: props?.width ? `${props.width}%` : '0%',
    transition: 'width .3s ease-in',
}));
